import axios, { AxiosRequestConfig } from 'axios';
import { getHostname } from '@/utils/get-hostname';
import { authService } from '@/services/auth.service';
import { currentLocale } from '@/plugins/i18n';

export interface ApiResponse<T> {
  success: boolean;
  data: T;
}

export type ApiMethod = <T>(
  url: string,
  body?: any,
  type?: AxiosRequestConfig['responseType'],
  headers?: Record<string, string>,
) => Promise<ApiResponse<T>>;

function getHeaders(headers: Record<string, string> = {}): Record<string, string> {
  headers['Content-Type'] = 'application/json';

  const accessToken = localStorage.getItem('accessToken');
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }
  headers['X-Community-Hostname'] = getHostname();
  // A caller may ask for another language than the interface's, such as the one a page is shown in.
  headers['Accept-Language'] ??= currentLocale();

  return headers;
}

// A FormData body brings its own multipart Content-Type, boundary included, which the JSON default would replace.
function dropJsonContentTypeForFormData(config: AxiosRequestConfig) {
  if (config.data instanceof FormData && config.headers) {
    delete config.headers['Content-Type'];
  }
}

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response.status === 401) {
      await authService.logoutUser();
      window.location.href = '/';
    } else if (error.response.status === 410) {
      location.reload();
    }
    return error;
  },
);

export const getRequest: ApiMethod = async <TResponse = any>(
  url: string,
  body?: any,
  type: AxiosRequestConfig['responseType'] = 'json',
  headers?: Record<string, string>,
): Promise<ApiResponse<TResponse>> => {
  const config: AxiosRequestConfig = {
    method: 'get',
    url: url,
    headers: getHeaders(headers),
    params: body,
    responseType: type,
  };

  return (await request(config)).data as ApiResponse<TResponse>;
};

export const postRequest: ApiMethod = async <TBody = any, TResponse = any>(
  url: string,
  body?: TBody,
  type: AxiosRequestConfig['responseType'] = 'json',
  headers?: Record<string, string>,
): Promise<ApiResponse<TResponse>> => {
  const config: AxiosRequestConfig = {
    method: 'post',
    url: url,
    headers: getHeaders(headers),
    data: body,
    responseType: type,
  };
  dropJsonContentTypeForFormData(config);

  return (await request(config)).data as ApiResponse<TResponse>;
};

export const putRequest: ApiMethod = async <TBody = any, TResponse = any>(
  url: string,
  body?: TBody,
  type: AxiosRequestConfig['responseType'] = 'json',
  headers?: Record<string, string>,
): Promise<ApiResponse<TResponse>> => {
  const config: AxiosRequestConfig = {
    method: 'put',
    url: url,
    headers: getHeaders(headers),
    data: body,
    responseType: type,
  };

  return (await request(config)).data as ApiResponse<TResponse>;
};

export const patchRequest: ApiMethod = async <TBody = any, TResponse = any>(
  url: string,
  body?: TBody,
  type: AxiosRequestConfig['responseType'] = 'json',
  headers?: Record<string, string>,
): Promise<ApiResponse<TResponse>> => {
  const config: AxiosRequestConfig = {
    method: 'patch',
    url: url,
    headers: getHeaders(headers),
    data: body,
    responseType: type,
  };

  return (await request(config)).data as ApiResponse<TResponse>;
};

export const deleteRequest: ApiMethod = async <TBody = any, TResponse = any>(
  url: string,
  body?: TBody,
  type: AxiosRequestConfig['responseType'] = 'json',
  headers?: Record<string, string>,
): Promise<ApiResponse<TResponse>> => {
  const config: AxiosRequestConfig = {
    method: 'delete',
    url: url,
    headers: getHeaders(headers),
    data: body,
    responseType: type,
  };

  return (await request(config)).data as ApiResponse<TResponse>;
};

export async function request(config: AxiosRequestConfig): Promise<any> {
  try {
    return await axios(config);
  } catch (err: any) {
    console.error(err);
    throw err;
  }
}

export class ApiError extends Error {
  status?: number;
  // The API's machine-readable reason, such as `document-limit`, when it gives one.
  code?: string;

  constructor(message: string, status?: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

/**
 * Sends a request and rejects with an `ApiError` on any failure. The response interceptor above resolves errors
 * instead of throwing them (COCO-3009), so the other helpers resolve with `undefined` on a 4xx or 5xx and a caller's
 * `catch` never runs.
 */
async function requestOrThrow<TResponse>(config: AxiosRequestConfig): Promise<ApiResponse<TResponse>> {
  dropJsonContentTypeForFormData(config);

  const result: any = await request(config);

  if (result?.isAxiosError || result?.response) {
    const data = result.response?.data;
    throw new ApiError(data?.message ?? result.message ?? 'Request failed', result.response?.status, data?.code);
  }

  // A 204 has no body to report its success in.
  if (result?.status === 204) {
    return { success: true, data: undefined as TResponse };
  }

  const payload = result?.data as (ApiResponse<TResponse> & { message?: string }) | undefined;
  if (!payload?.success) {
    throw new ApiError(payload?.message ?? 'Request failed', result?.status);
  }

  return payload;
}

/** A POST that rejects with an `ApiError` on any failure. */
export const postOrThrow = <TResponse = any>(
  url: string,
  body?: any,
  headers?: Record<string, string>,
): Promise<ApiResponse<TResponse>> =>
  requestOrThrow<TResponse>({
    method: 'post',
    url: url,
    headers: getHeaders(headers),
    data: body,
    responseType: 'json',
  });

/** A DELETE that rejects with an `ApiError` on any failure. */
export const deleteOrThrow = <TResponse = any>(
  url: string,
  headers?: Record<string, string>,
): Promise<ApiResponse<TResponse>> =>
  requestOrThrow<TResponse>({
    method: 'delete',
    url: url,
    headers: getHeaders(headers),
    responseType: 'json',
  });

export default {
  get: getRequest,
  post: postRequest,
  put: putRequest,
  patch: patchRequest,
  delete: deleteRequest,
};
